import axios from 'axios';
import type { AuthCredentials, RegisterData, AuthResponse, ApiResponse } from '@/types';
import { storage, STORAGE_KEYS } from '@/utils/storage';

const websiteApiBaseUrl = (import.meta.env.VITE_WEBSITE_API_BASE_URL || 'https://api.beautysports.in/website-api').replace(/\/$/, '');

function apiError(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as { error?: { message?: string; details?: { fields?: Record<string, string> } } } | undefined;
    const message = payload?.error?.message || fallback;
    const fields = payload?.error?.details?.fields || {};
    return Object.assign(new Error(message), { fields });
  }
  return error instanceof Error ? error : new Error(fallback);
}

function persistAuth(data: AuthResponse) {
  storage.set(STORAGE_KEYS.AUTH_TOKEN, data.token);
  storage.set(STORAGE_KEYS.AUTH_USER, data.user);
}

export const authService = {
  login: async (credentials: AuthCredentials): Promise<ApiResponse<AuthResponse>> => {
    try {
      const response = await axios.post<{ data: AuthResponse }>(`${websiteApiBaseUrl}/auth/login`, credentials);
      persistAuth(response.data.data);
      return { data: response.data.data, message: 'Login successful', success: true };
    } catch (error) {
      throw apiError(error, 'Unable to login.');
    }
  },

  register: async (data: RegisterData): Promise<ApiResponse<AuthResponse>> => {
    try {
      const response = await axios.post<{ data: AuthResponse }>(`${websiteApiBaseUrl}/auth/register`, data);
      persistAuth(response.data.data);
      return { data: response.data.data, message: 'Account created successfully', success: true };
    } catch (error) {
      throw apiError(error, 'Unable to create account.');
    }
  },

  forgotPassword: async (email: string): Promise<ApiResponse<{ email: string; securityQuestion: string; securityQuestionText: string }>> => {
    try {
      const response = await axios.post<{ data: { email: string; securityQuestion: string; securityQuestionText: string } }>(`${websiteApiBaseUrl}/auth/forgot-password`, { email });
      return { data: response.data.data, message: 'Security question loaded', success: true };
    } catch (error) {
      throw apiError(error, 'Unable to load security question.');
    }
  },

  verifyOtp: async (_otp: string): Promise<ApiResponse<null>> => {
    throw new Error('OTP verification is not available.');
  },

  resetPassword: async (data: { email: string; securityAnswer: string; password: string; confirmPassword: string }): Promise<ApiResponse<null>> => {
    try {
      await axios.post(`${websiteApiBaseUrl}/auth/reset-password`, data);
      return { data: null, message: 'Password reset successful', success: true };
    } catch (error) {
      throw apiError(error, 'Unable to reset password.');
    }
  },

  logout: async (): Promise<void> => {
    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
    storage.remove(STORAGE_KEYS.AUTH_USER);
  },
};
